"use client";

import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";

import { setCookieClient } from "./cookie-client";
import type { CookieJarKey, CookieJarValue } from "./cookie-jar-configs";

export type CookieJarValues = {
  [K in CookieJarKey]: CookieJarValue<K>;
};

interface CookieJarContextValue {
  values: CookieJarValues;
  setValue: <K extends CookieJarKey>(name: K, value: CookieJarValue<K>) => void;
}

const CookieJarContext = createContext<CookieJarContextValue | null>(null);

interface CookieJarProviderProps {
  initialValues: CookieJarValues;
  children: ReactNode;
}

export function CookieJarProvider({ initialValues, children }: CookieJarProviderProps) {
  const [values, setValues] = useState<CookieJarValues>(initialValues);

  const setValue = useCallback(<K extends CookieJarKey>(name: K, value: CookieJarValue<K>) => {
    setCookieClient(name, value);
    setValues((prev) => (prev[name] === value ? prev : { ...prev, [name]: value }));
  }, []);

  const contextValue = useMemo<CookieJarContextValue>(
    () => ({ values, setValue }),
    [values, setValue],
  );

  return <CookieJarContext.Provider value={contextValue}>{children}</CookieJarContext.Provider>;
}

export function useCookieJar(): CookieJarContextValue {
  const ctx = useContext(CookieJarContext);
  if (!ctx) {
    throw new Error("useCookieJar must be used within a CookieJarProvider");
  }
  return ctx;
}
